import { AppStore } from '../store/AppStore';
import { EFormCheckboxLabelFormat, EPropertyOrigin } from '../data/Constants';
import { resetError } from '../store/states/ErrorSlice';
import { t, TFunction } from 'i18next';
import { resetAlertMessage } from '../store/states/AlertMessageSlice';
import { resetDialogMessage } from '../store/states/DialogSlice';
import { FieldErrors } from 'react-hook-form';

import type {
  IFormRule,
  IEnumOption,
  IEnumValue,
  IFeature,
  IFeaturePropertyAssociationRow,
  IProduct,
  IProductPropertyAssociationRow,
  IPropertyData,
} from '../../types';
import { updateTableData } from '../store/utilities/SliceDataHelper';

//update feature property Association data to the state
export function updateFeaturePropertyAssociation(state, payload) {
  return updateTableData(state, payload);
}

//To derive deletion message
export const joinSelectedKeys = (keySelected) => {
  return keySelected.join(', ') + '.';
};

export const formatDateString = (dateValue: string) => {
  const date = new Date(dateValue);
  const month = ('0' + (date.getMonth() + 1)).slice(-2);
  const day = ('0' + date.getDate()).slice(-2);
  const year = date.getFullYear();
  const hour = ('0' + date.getHours()).slice(-2);
  const min = ('0' + date.getMinutes()).slice(-2);
  const seg = ('0' + date.getSeconds()).slice(-2);
  return month + '-' + day + '-' + year + ' ' + hour + ':' + min + ':' + seg + ' UTC';
};

/**
 * To get the maximum date from given dates in string Array
 * @param {string[]} datesArray Array of dates
 * @returns {string} Maximum date in Formatted way
 */
export const getMaxDate = (datesArray: string[]) => {
  return datesArray.length > 0
    ? formatDateString(datesArray.reduce((a, b) => (a > b ? a : b), ''))
    : '';
};

//To loop through all features and options and Create Rows data to display on Features table
export const getFeaturePropertyRows = (features: IFeature[], searchKey: string) => {
  const rows: IFeaturePropertyAssociationRow[] = [];
  features.sort((a, b) => a?.featureCode?.localeCompare(b.featureCode));
  let featureRow = {};
  features?.forEach((feature) => {
    if (!searchKey && feature.featureCode) {
      featureRow = {
        code: feature.featureCode,
        description: feature.featureName,
        hasOption: feature?.options?.length > 0,
      };
      feature.associations?.map((property) => {
        featureRow[property.code] = property.name;
      });

      rows.push(featureRow);

      if (feature.options) {
        feature.hasOption = false;
        let optionRow = {};
        feature.options.forEach((option) => {
          optionRow = {
            code: option.optionCode,
            description: option.optionName,
          };
          option?.associations?.map((property) => {
            optionRow[property.code] = property.name;
          });

          rows.push(optionRow);
        });
      }
    } else if (searchKey) {
      getSearchFeatureAssociationRows(feature, searchKey, rows);
    }
  });

  return rows;
};

//Get the Product Property association rows from the api response
export const getProductPropertyAssociationRows = (products: IProduct[]) => {
  const rows: IProductPropertyAssociationRow[] = [];
  products?.forEach((product: IProduct) => {
    product['description'] = product.productName;
    product.associations?.map((property) => {
      product[property.code] = property.name;
    });
    rows.push(product);
  });
  return rows;
};

//Get Search Feature/option rows from response
export const getSearchFeatureAssociationRows = (feature, searchKey, rows) => {
  const isFeatureSearch =
    feature?.featureCode?.toLowerCase().includes(searchKey.toLowerCase()) ||
    feature?.featureName.toLowerCase()?.includes(searchKey.toLowerCase());
  if (isFeatureSearch) {
    const searchFeatureRow = {
      code: feature.featureCode,
      description: feature.featureName,
    };
    feature?.associations?.map((property) => {
      searchFeatureRow[property.code] = property.name;
    });
    rows.push(searchFeatureRow);
  } else {
    const option = feature?.options?.find(
      (opt) =>
        opt.optionCode.toLowerCase()?.includes(searchKey.toLowerCase()) ||
        opt?.optionName?.toLowerCase().includes(searchKey.toLowerCase())
    );
    if (option) {
      const searchOptionRow = {
        code: option.optionCode,
        description: option.optionName,
      };
      option?.associations?.map((property) => {
        searchOptionRow[property.code] = property.name;
      });
      rows.push(searchOptionRow);
    }
  }
  return rows;
};

//returns the response from api in paginated format
export function handlePaginatedData(response, action) {
  const total = response.headers['x-pagination']
    ? JSON.parse(response.headers['x-pagination']).total
    : 0;

  return {
    data: response.data ? response.data : [],
    searchKey: action.searchCode,
    page: action.page,
    totalRecords: total || 0,
    recordsPerPage: action.limit,
    skipUpdate: action.skipUpdate,
    caller: action.caller,
  };
}

//Get enum values option from the lov list
export const getEnumOptions = (
  enumValues: IEnumValue[],
  value: string | boolean
): IEnumOption[] => {
  const values = enumValues.find((v: IEnumValue) => v.code === value);
  return values ? values.options : [];
};

//Set the Properties columns data
export function setColumnsData(properties: IPropertyData[], setColumns, tableColumns) {
  const cols = [...tableColumns];
  properties.forEach((property) => {
    if (property.origin === EPropertyOrigin.Pdm && property.isActive) {
      cols.push({
        field: property?.code,
        width: 100,
        hasNoTranslation: true,
        applicability: property.applicability,
      });
    }
  });
  setColumns(cols);
}

//To Reset Error and Alert messages
export function resetAllErrorInfo() {
  AppStore.dispatch(resetDialogMessage());
  AppStore.dispatch(resetAlertMessage());
  AppStore.dispatch(resetError());
}

/**
 * Gets the appropriate error message for a form field based on validation errors
 *
 * @param {FieldErrors} errors - The form errors object from react-hook-form
 * @param {string} formField - The name of the form field to get error message for
 * @param {TFunction} t - The translation function from i18next
 * @param {Object} validation - Validation rules object
 * @param {Object} [validation.minLength] - Minimum length validation rule
 * @param {number} validation.minLength.value - The minimum length value
 * @param {Object} [validation.maxLength] - Maximum length validation rule
 * @param {number} validation.maxLength.value - The maximum length value
 * @returns {string} The translated error message with appropriate parameters
 *
 * @example
 * const errorMsg = getErrorMessage(
 *   formErrors,
 *   'username',
 *   t,
 *   { minLength: { value: 3 }, maxLength: { value: 20 } }
 * );
 */
export const getErrorMessage = (
  errors: FieldErrors,
  formField: string,
  t: TFunction,
  validation: IFormRule
) => {
  if (errors[formField]?.type === 'maxLength') {
    return t(
      `messages.${typeof errors[formField]?.message === 'string' ? errors[formField]?.message : 'invalidInputErrorMessage'}`,
      { limit: validation.maxLength?.value }
    );
  } else if (errors[formField]?.type === 'minLength') {
    return t(
      `messages.${typeof errors[formField]?.message === 'string' ? errors[formField]?.message : 'invalidInputErrorMessage'}`,
      { limit: validation.minLength?.value }
    );
  } else {
    return t(
      `messages.${typeof errors[formField]?.message === 'string' ? errors[formField]?.message : 'invalidInputErrorMessage'}`
    );
  }
};

/**
 * Generates a formatted label for checkbox elements based on the specified label format
 *
 * @param {string} labelFormat - The format type for the label (from EFormCheckboxLabelFormat enum)
 * @param {string} code - The code value to be used in the label
 * @param {string} name - The name value to be used in the label
 * @returns {string} The formatted label string based on the specified format
 *
 * @description
 * This function creates checkbox labels in different formats based on the labelFormat parameter:
 * - Code format: Returns only the code value
 * - Name format: Returns only the name value
 * - CodeWithName format: Returns code followed by name in parentheses
 *
 * The function uses the EFormCheckboxLabelFormat enum to determine the output format,
 * providing consistent labeling across checkbox components in forms.
 */
export function getLabelForCheckbox(
  labelFormat: string | undefined,
  code: string,
  name: string
): string {
  let result = name;
  if (labelFormat === EFormCheckboxLabelFormat.Code) {
    result = code;
  } else if (labelFormat === EFormCheckboxLabelFormat.Name) {
    result = t(`${'labels.' + name}`);
  } else if (labelFormat === EFormCheckboxLabelFormat.CodeWithName) {
    result = `${code} (${name})`;
  }
  return result;
}
